import React, { useEffect, useState, useRef } from 'react';
import {
  Container, Paper, Box, Typography, Button, Alert, CircularProgress,
  Divider, AppBar, Toolbar, Snackbar, IconButton, useTheme, useMediaQuery,
  Chip, Stack
} from '@mui/material';
import { CheckCircle, Download, Edit, Close } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logo from '../../components/Sidebar/logo.png';

const API_URL = process.env.REACT_APP_API_URL;

export default function ContratoAssinatura() {
  const { token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [contrato, setContrato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [assinando, setAssinando] = useState(false);
  const [assinado, setAssinado] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const canvasRef = useRef(null);
  const [desenhando, setDesenhando] = useState(false);
  const [pontos, setPontos] = useState([]);

  useEffect(() => {
    carregarContrato();
  }, [token]);

  const carregarContrato = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/contratos/token/${token}`);
      
      // Limpar o HTML removendo o placeholder de assinatura
      let htmlLimpo = response.data.conteudo_html;
      
      // Remover o div "ESPAÇO PARA ASSINATURA DIGITAL"
      htmlLimpo = htmlLimpo.replace(
        /<div[^>]*>ESPAÇO PARA ASSINATURA DIGITAL<\/div>/gi, 
        ''
      );
      
      // Remover qualquer variação do placeholder
      htmlLimpo = htmlLimpo.replace(
        /<div[^>]*style="[^"]*"[^>]*>[\s\S]*?ESPAÇO PARA ASSINATURA[\s\S]*?<\/div>/gi,
        ''
      );
      
      setContrato({...response.data, conteudo_html: htmlLimpo});
      setAssinado(response.data.status === 'assinado');
    } catch (error) {
      console.error('Erro ao carregar contrato:', error);
      setErro('Contrato não encontrado ou link inválido.');
    } finally {
      setLoading(false);
    }
  };

  const iniciarDesenho = (e) => {
    if (assinado) return;
    setDesenhando(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    setPontos([{ x, y }]);
  };

  const desenhar = (e) => {
    if (!desenhando || assinado) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (pontos.length > 0) {
      const ultimoPonto = pontos[pontos.length - 1];
      ctx.beginPath();
      ctx.moveTo(ultimoPonto.x, ultimoPonto.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    setPontos([...pontos, { x, y }]);
  };

  const pararDesenho = () => {
    setDesenhando(false);
  };

  const limparAssinatura = () => {
    if (assinado) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setPontos([]);
  };

  const assinarContrato = async () => {
    if (pontos.length < 10) {
      setSnackbar({ 
        open: true, 
        message: 'Por favor, desenhe sua assinatura no campo acima.', 
        severity: 'warning' 
      });
      return;
    }

    try {
      setAssinando(true);
      const canvas = canvasRef.current;
      const assinaturaBase64 = canvas.toDataURL('image/png');

      await axios.post(`${API_URL}/contratos/assinar/${token}`, {
        assinatura_digital: assinaturaBase64
      });

      setAssinado(true);
      setSnackbar({ 
        open: true, 
        message: '✅ Contrato assinado com sucesso!', 
        severity: 'success' 
      });
      carregarContrato();
    } catch (error) {
      console.error('Erro ao assinar contrato:', error);
      setSnackbar({ 
        open: true, 
        message: 'Erro ao assinar contrato. Tente novamente.', 
        severity: 'error' 
      });
    } finally {
      setAssinando(false);
    }
  };

  const baixarPDF = async () => {
    try {
      setSnackbar({ open: true, message: 'Gerando PDF...', severity: 'info' });

      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = `
        position: absolute;
        left: -9999px;
        top: 0;
        width: 794px;
        background-color: #ffffff;
        font-family: Arial, sans-serif;
        font-size: 11pt;
        line-height: 1.7;
        color: #333;
      `;

      const header = document.createElement('div');
      header.style.cssText = 'border-bottom: 3px solid #ED3237; padding-bottom: 15px; margin-bottom: 20px; text-align: center;';
      header.innerHTML = `
        <h1 style="margin: 0; color: #ED3237; font-size: 22pt; font-weight: bold;">DELTA RENT CAR</h1>
        <p style="margin: 5px 0; font-size: 9pt; color: #666;">Contrato de Locação de Veículo</p>
        <p style="margin: 5px 0; font-size: 9pt; color: #666;">Contrato Nº ${contrato.id}</p>
      `;

      const conteudo = document.createElement('div');
      conteudo.style.cssText = 'margin-top: 20px; margin-bottom: 30px;';

      let htmlLimpo = contrato.conteudo_html;
      htmlLimpo = htmlLimpo.replace(/<div[^>]*>ESPAÇO PARA ASSINATURA DIGITAL<\/div>/gi, '');
      htmlLimpo = htmlLimpo.replace(/<div[^>]*style="[^"]*"[^>]*>[\s\S]*?ESPAÇO PARA ASSINATURA[\s\S]*?<\/div>/gi, '');
      conteudo.innerHTML = htmlLimpo;

      conteudo.querySelectorAll('p').forEach(p => {
        p.style.textAlign = 'justify';
        p.style.marginBottom = '10px';
        p.style.lineHeight = '1.7';
      });

      const assinaturaSection = document.createElement('div');
      assinaturaSection.style.cssText = 'margin-top: 40px; page-break-inside: avoid;';
      if (assinado && contrato.assinatura_digital) {
        assinaturaSection.innerHTML = `
          <div style="border-top: 2px solid #e0e0e0; padding-top: 20px;">
            <h3 style="color: #ED3237; font-size: 13pt; margin-bottom: 12px;">ASSINATURA DIGITAL DO LOCATÁRIO</h3>
            <div style="border: 2px solid #4caf50; padding: 12px; background-color: #f9fff9; border-radius: 6px; display: inline-block;">
              <img src="${contrato.assinatura_digital}" alt="Assinatura" style="max-height: 90px; max-width: 300px; display: block;" />
            </div>
            <p style="margin-top: 8px; font-size: 9pt; color: #555;">
              <strong>Data:</strong> ${new Date(contrato.data_assinatura).toLocaleString('pt-BR')}<br/>
              <strong>IP:</strong> ${contrato.ip_assinatura || 'Não registrado'}
            </p>
          </div>
        `;
      } else {
        assinaturaSection.innerHTML = `
          <div style="margin-top: 50px; text-align: center;">
            <div style="border-top: 1px solid #000; width: 300px; margin: 0 auto 8px; padding-top: 6px; font-size: 10pt;">
              ASSINATURA DO LOCATÁRIO
            </div>
          </div>
        `;
      }

      const footer = document.createElement('div');
      footer.style.cssText = 'border-top: 2px solid #ED3237; padding-top: 12px; margin-top: 30px; text-align: center; font-size: 8pt; color: #666;';
      footer.innerHTML = `
        <p style="margin: 4px 0;"><strong>Delta Rent Car LTDA</strong></p>
        <p style="margin: 4px 0;">contato@deltarentcar.com.br | (92) 3000-0000</p>
        <p style="margin: 4px 0;">Documento gerado digitalmente em ${new Date().toLocaleString('pt-BR')}</p>
      `;

      tempContainer.appendChild(header);
      tempContainer.appendChild(conteudo);
      tempContainer.appendChild(assinaturaSection);
      tempContainer.appendChild(footer);
      document.body.appendChild(tempContainer);

      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempContainer);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

      const pdfWidth = pdf.internal.pageSize.getWidth();   // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

      const marginX = 20;      // margem esquerda e direita (mm)
      const marginTop = 25;    // margem superior (mm)
      const marginBottom = 20; // margem inferior (mm)

      const contentWidth = pdfWidth - 2 * marginX;              // 170mm
      const usableHeight = pdfHeight - marginTop - marginBottom; // 252mm

      const ratio = contentWidth / canvas.width; // mm por pixel
      const totalScaledHeight = canvas.height * ratio;

      if (totalScaledHeight <= usableHeight) {
        pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', marginX, marginTop, contentWidth, totalScaledHeight);
      } else {
        const pixelsPerPage = Math.floor(usableHeight / ratio);
        let canvasY = 0;
        let pageNum = 0;

        while (canvasY < canvas.height) {
          if (pageNum > 0) pdf.addPage();

          const slicePixels = Math.min(canvas.height - canvasY, pixelsPerPage);

          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = slicePixels;

          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, canvasY, canvas.width, slicePixels, 0, 0, canvas.width, slicePixels);

          const scaledSliceHeight = slicePixels * ratio;
          pdf.addImage(pageCanvas.toDataURL('image/png', 1.0), 'PNG', marginX, marginTop, contentWidth, scaledSliceHeight);

          canvasY += slicePixels;
          pageNum++;
        }
      }

      pdf.save(`Contrato_${contrato.id}_Delta_Rent_Car.pdf`);
      setSnackbar({ open: true, message: '✅ PDF baixado com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setSnackbar({ open: true, message: 'Erro ao gerar PDF.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: '#3e4095' }}>
          <Toolbar sx={{ justifyContent: 'center', py: 1.5 }}>
            <img src={logo} alt="Delta Rent Car" style={{ height: isMobile ? 40 : 50 }} />
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 3, color: 'text.secondary' }}>Carregando contrato...</Typography>
        </Container>
      </Box>
    );
  }

  if (erro) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: '#3e4095' }}>
          <Toolbar sx={{ justifyContent: 'center', py: 1.5 }}>
            <img src={logo} alt="Delta Rent Car" style={{ height: isMobile ? 40 : 50 }} />
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ fontSize: '1.1rem' }}>{erro}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header Profissional com Fundo Preto */}
      <AppBar position="static" elevation={2} sx={{ bgcolor: '#3e4095' }}>
        <Toolbar sx={{ justifyContent: 'center', py: 1.5 }}>
          <img src={logo} alt="Delta Rent Car" style={{ height: isMobile ? 40 : 50 }} />
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          {/* Cabeçalho do Contrato */}
          <Box 
            sx={{ 
              bgcolor: assinado ? '#4caf50' : '#1976d2', 
              color: 'white', 
              p: { xs: 2, md: 3 },
              textAlign: 'center'
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
              {assinado ? '✓ Contrato Assinado Digitalmente' : 'Contrato de Locação'}
            </Typography>
            {assinado && contrato.data_assinatura && (
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Assinado em {new Date(contrato.data_assinatura).toLocaleString('pt-BR')}
              </Typography>
            )}
          </Box>

          {/* Status Badge */}
          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Chip 
                label={assinado ? 'Assinado' : 'Aguardando Assinatura'} 
                color={assinado ? 'success' : 'warning'}
                icon={assinado ? <CheckCircle /> : undefined}
              />
              <Chip label={`Contrato #${contrato.id}`} variant="outlined" />
            </Stack>
          </Box>

          {/* Conteúdo do Contrato - Estilizado */}
          <Box 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              bgcolor: 'white',
              maxHeight: { xs: '400px', md: '600px' }, 
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
              '&::-webkit-scrollbar-thumb': { background: '#ED3237', borderRadius: '4px' }
            }}
          >
            <div 
              id="contrato-conteudo"
              dangerouslySetInnerHTML={{ __html: contrato?.conteudo_html }}
              style={{ 
                lineHeight: '1.8',
                fontSize: isMobile ? '14px' : '16px',
                color: '#333'
              }}
            />
            
            {/* Mostrar Assinatura se já foi assinado */}
            {assinado && contrato.assinatura_digital && (
              <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #e0e0e0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Assinatura Digital
                </Typography>
                <Box 
                  sx={{ 
                    border: '2px solid #4caf50', 
                    borderRadius: 2, 
                    p: 2, 
                    bgcolor: '#f9f9f9',
                    display: 'inline-block',
                    maxWidth: '100%'
                  }}
                >
                  <img 
                    src={contrato.assinatura_digital} 
                    alt="Assinatura" 
                    style={{ 
                      maxWidth: '100%', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Assinado digitalmente em {new Date(contrato.data_assinatura).toLocaleString('pt-BR')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Área de Assinatura - Design Melhorado */}
          {!assinado && (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                📝 Assinatura Digital
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Desenhe sua assinatura no campo abaixo usando o mouse ou toque:
              </Typography>
              <Box
                sx={{
                  border: '3px dashed #1976d2',
                  borderRadius: 2,
                  mb: 2,
                  position: 'relative',
                  backgroundColor: 'white',
                  overflow: 'hidden'
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={250}
                  onMouseDown={iniciarDesenho}
                  onMouseMove={desenhar}
                  onMouseUp={pararDesenho}
                  onMouseLeave={pararDesenho}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    iniciarDesenho({ clientX: touch.clientX, clientY: touch.clientY });
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    desenhar({ clientX: touch.clientX, clientY: touch.clientY });
                  }}
                  onTouchEnd={pararDesenho}
                  style={{
                    cursor: 'crosshair',
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    touchAction: 'none'
                  }}
                />
              </Box>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={limparAssinatura}
                  startIcon={<Edit />}
                  sx={{ height: 48 }}
                >
                  Limpar
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={assinarContrato}
                  disabled={assinando || pontos.length < 10}
                  startIcon={<CheckCircle />}
                  sx={{ height: 48, fontWeight: 'bold' }}
                >
                  {assinando ? 'Assinando...' : 'Confirmar Assinatura'}
                </Button>
              </Stack>
            </Box>
          )}

          {/* Botão Download PDF */}
          {assinado && (
            <Box sx={{ p: { xs: 2, md: 3 }, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={baixarPDF}
                startIcon={<Download />}
                size="large"
                sx={{ 
                  minWidth: { xs: '100%', sm: 300 }, 
                  height: 50,
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Baixar Contrato em PDF
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          action={
            <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
